/**
 * createRobotState() — framework-free composition of the robot business-state slices.
 *
 * Each call returns an INDEPENDENT set of slices, so a host can create one instance
 * per session/robot. The UI scopes it per session via React context
 * (`RobotStateProvider` / `useRobotState`) and ingests inbound data through
 * `attachSession`, so robot state never leaks across sessions/tests.
 */

import { createRobotConfigSlice } from "./robot-config";
import type { RobotConfigSlice } from "./robot-config";
import { createRobotTelemetrySlice } from "./robot-telemetry";
import type { RobotTelemetrySlice } from "./robot-telemetry";
import { createSpeedLimitsSlice } from "./speed-limits";
import type { SpeedLimitsSlice } from "./speed-limits";
import { createLinkQualitySlice } from "../connection-stats";
import { createControlStatusSlice } from "./control-status";
import type { ControlStatusSlice } from "./control-status";
import { createArmStateSlice } from "./arm-state";
import type { ArmStateSlice } from "./arm-state";
import type { LinkQualitySlice } from "../connection-stats";
import { createCameraVideoStatsSlice, type CameraVideoStatsSlice } from "./camera-video-stats";

export interface RobotState {
  robotConfig: RobotConfigSlice;
  robotTelemetry: RobotTelemetrySlice;
  speedLimits: SpeedLimitsSlice;
  linkQuality: LinkQualitySlice;
  /** Backend control status + last rejected-message error (PROTOCOL.md §4.2/§4.4). */
  controlStatus: ControlStatusSlice;
  /** Planner arm state: joint-state + goal events (PROTOCOL.md §4.6/§4.7). */
  armState: ArmStateSlice;
  cameraVideoStats: CameraVideoStatsSlice;
}

export function createRobotState(): RobotState {
  const robotConfig = createRobotConfigSlice();
  return {
    robotConfig,
    robotTelemetry: createRobotTelemetrySlice(),
    speedLimits: createSpeedLimitsSlice(robotConfig),
    linkQuality: createLinkQualitySlice(),
    controlStatus: createControlStatusSlice(),
    armState: createArmStateSlice(),
    cameraVideoStats: createCameraVideoStatsSlice(),
  };
}
